import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * PeopleAlsoAsk - 2026 AIO-Optimized FAQ Component
 * 
 * Designed to match Google's "People Also Ask" format for maximum extraction potential.
 * Features:
 * - Expandable accordion-style questions
 * - Schema-friendly markup structure
 * - Conversational question format (2026 requirement)
 * - Links to related content for topic authority
 * 
 * Google's 2026 AIO heavily weights PAA sections for follow-up queries
 */
export const PeopleAlsoAsk = ({ 
  faqItems = [],
  title = 'People Also Ask',
  showRelatedLinks = true,
  variant = 'default' // 'default', 'compact', 'sidebar'
}) => {
  const [openIndex, setOpenIndex] = useState(null);

  if (!faqItems || faqItems.length === 0) return null;

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const containerStyles = {
    default: 'bg-card/30 border border-white/10 rounded-xl p-6',
    compact: 'bg-transparent',
    sidebar: 'bg-card/50 border border-white/10 rounded-lg p-4'
  };

  return (
    <section 
      className={`${containerStyles[variant]} my-8`}
      itemScope 
      itemType="https://schema.org/FAQPage"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-primary/20 rounded-lg">
          <HelpCircle className="w-5 h-5 text-primary" />
        </div>
        <h2 className="text-xl font-bold">{title}</h2>
      </div>

      {/* FAQ Items */}
      <div className="space-y-3">
        {faqItems.map((item, index) => (
          <div
            key={index}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="border border-white/10 rounded-lg overflow-hidden hover:border-primary/30 transition"
          >
            {/* Question - Clickable header */}
            <button
              onClick={() => toggleItem(index)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition"
              aria-expanded={openIndex === index}
            >
              <span 
                itemProp="name" 
                className="font-medium pr-4"
              >
                {item.question}
              </span>
              <motion.div
                animate={{ rotate: openIndex === index ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              </motion.div>
            </button>

            {/* Answer - Expandable content */}
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <div className="px-4 pb-4 pt-2 border-t border-white/10 bg-white/5">
                    <p 
                      itemProp="text" 
                      className="text-muted-foreground leading-relaxed"
                    >
                      {item.answer}
                    </p>
                    
                    {/* Related Link (if provided) */}
                    {showRelatedLinks && item.relatedLink && (
                      <Link
                        to={item.relatedLink}
                        className="inline-flex items-center gap-2 mt-3 text-sm text-primary hover:underline"
                      >
                        Learn more →
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* Footer - Encourage engagement */}
      <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between text-sm text-muted-foreground">
        <span>Have more questions?</span>
        <a 
          href="https://xtreamcube.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-primary hover:underline"
        >
          <MessageCircle className="w-4 h-4" />
          Contact Support
        </a>
      </div>
    </section>
  );
};

/**
 * PeopleAlsoAskCompact - Inline version for within article content
 */
export const PeopleAlsoAskCompact = ({ faqItems = [], maxItems = 3 }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const displayItems = faqItems.slice(0, maxItems);

  if (displayItems.length === 0) return null;

  return (
    <div className="my-6 p-4 bg-card/30 border-l-4 border-primary rounded-r-lg">
      <h3 className="text-sm font-semibold text-primary mb-3">Related Questions:</h3>
      <div className="space-y-2">
        {displayItems.map((item, index) => (
          <div key={index}>
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="text-left text-sm font-medium hover:text-primary transition w-full flex items-center gap-2"
            >
              <ChevronDown 
                className={`w-4 h-4 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} 
              />
              {item.question}
            </button>
            {openIndex === index && (
              <p className="text-sm text-muted-foreground mt-2 ml-6">
                {item.answer}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * PeopleAlsoAskStatic - Non-interactive version for SEO
 * All questions and answers visible (best for initial page load SEO)
 */
export const PeopleAlsoAskStatic = ({ faqItems = [], title = 'Frequently Asked Questions' }) => {
  if (!faqItems || faqItems.length === 0) return null;

  return (
    <section 
      className="my-8"
      itemScope 
      itemType="https://schema.org/FAQPage"
    >
      <h2 className="text-2xl font-bold mb-6">{title}</h2>
      <div className="space-y-6">
        {faqItems.map((item, index) => (
          <div
            key={index}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="border-b border-white/10 pb-6 last:border-0"
          >
            <h3 
              itemProp="name" 
              className="font-semibold text-lg mb-2"
            >
              {item.question}
            </h3>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
            >
              <p 
                itemProp="text" 
                className="text-muted-foreground leading-relaxed"
              >
                {item.answer}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * Generate conversational FAQ questions for 2026 AIO
 * Converts basic questions into natural language queries
 */
export const generateConversationalFAQ = (topic, baseQuestions) => {
  const conversationalPrefixes = [
    'What is the best way to',
    'How do I',
    'Why should I',
    'Is it possible to',
    'What happens when',
    'Can I',
    'Which is better for'
  ];

  return baseQuestions.map((q, index) => ({
    ...q,
    question: q.question.startsWith('How') || q.question.startsWith('What') || q.question.startsWith('Why')
      ? q.question
      : `${conversationalPrefixes[index % conversationalPrefixes.length]} ${q.question.toLowerCase()}?`
  }));
};

/**
 * Default FAQ items for IPTV articles
 * Use as fallback when article-specific FAQ not available
 */
export const DEFAULT_IPTV_FAQ = [
  {
    question: "What is the best IPTV service in 2026?",
    answer: "XtreamCube is rated as the best IPTV service in 2026, offering 20,000+ channels, 4K streaming, and 24/7 support. With 27.5K+ active clients and a 95% retention rate, it's the most trusted IPTV provider.",
    relatedLink: "/blog/best-iptv-service-2026"
  },
  {
    question: "How do I get a free IPTV trial?",
    answer: "You can get a free 24-hour IPTV trial from XtreamCube with no credit card required. Simply visit the free trial page, enter your details, and receive instant access to test all premium features.",
    relatedLink: "/blog/iptv-free-trial-how-to-get-started"
  },
  {
    question: "Is IPTV legal in my country?",
    answer: "IPTV services are legal when they have proper licensing agreements for their content. XtreamCube operates as a legitimate IPTV provider. Check your local regulations for specific guidance.",
    relatedLink: "/blog/iptv-legal-legitimate-service"
  },
  {
    question: "What devices work with IPTV?",
    answer: "IPTV works on most devices including Amazon Firestick, Android TV, Samsung Smart TV, LG TV, Apple TV, iOS, Android phones, Windows PC, and Mac. Most modern streaming devices support IPTV apps.",
    relatedLink: "/blog/iptv-on-firestick-roku-appletv"
  },
  {
    question: "Why is my IPTV buffering?",
    answer: "IPTV buffering is usually caused by slow internet speeds, network congestion, or ISP throttling. Ensure you have at least 25Mbps for 4K streaming, use a wired connection, and consider a VPN if your ISP throttles streaming.",
    relatedLink: "/blog/iptv-troubleshooting-common-issues"
  }
];

export default PeopleAlsoAsk;
